/**
 * Mastery tracking hook with SM-2 spaced repetition.
 * session-hooksSKILL1.md — Layer 4, File 5.
 *
 * Rules:
 *  6. Mastery score range: 0–100 — never below 0, never above 100
 *  7. SM-2 only updates on session complete — not per-answer; interval capped at 30 days (D-024)
 *  8. All Supabase writes wrapped in try/catch — never crash UI
 */
import { useState, useEffect, useCallback } from 'react'
import { supabase }                          from '@/lib/supabase'
import type { MasteryEntry }                 from '@/hooks/session-types'

// ── SM-2 Algorithm (public domain SuperMemo) ──────────────────────────────────
// Quality 0-5 maps to session accuracy:
//   5 = 90-100% | 4 = 70-89% | 3 = 60-69% | 2 = 40-59% | 1 = 20-39% | 0 = <20%
//
// I3: consecutive_struggles tracks how many consecutive quality < 3 sessions
// a student has had on this topic without recovering. When >= 5, the interval
// is capped at 3 days instead of 1 — giving breathing room while keeping the
// topic in active review. Breaks the every-day death loop.
const STRUGGLE_THRESHOLD   = 5   // I3: consecutive failures before interval floor kicks in
const STRUGGLE_INTERVAL_CAP = 3  // I3: max interval (days) when student is stuck

function calculateSM2(
  quality:             number,
  ef:                  number,   // ease factor (default 2.5)
  interval:            number,   // days until next review
  reps:                number,   // successful repetition count
  consecutiveStruggles: number   // I3: current consecutive struggle count
) {
  let newEf        = ef
  let newInt       = interval
  let newRep       = reps
  let newStruggles = consecutiveStruggles

  if (quality >= 3) {
    newInt     = reps === 0 ? 1 : reps === 1 ? 6 : Math.round(interval * ef)
    newInt     = Math.min(newInt, 30)   // D-024: cap at 30 days
    newRep     = reps + 1
    newEf      = ef + (0.1 - (5 - quality) * (0.08 + (5 - quality) * 0.02))
    newStruggles = 0  // I3: recovery — reset struggle counter
  } else {
    // Quality < 3 → reset repetition count; interval depends on struggle count
    newRep       = 0
    newStruggles = consecutiveStruggles + 1
    // I3: if stuck for >= STRUGGLE_THRESHOLD sessions, cap at 3 days instead of 1
    // so the student gets breathing room rather than the same topic every day
    newInt = newStruggles >= STRUGGLE_THRESHOLD ? STRUGGLE_INTERVAL_CAP : 1
  }

  newEf = Math.max(1.3, Math.round(newEf * 100) / 100)   // EF floor: 1.3

  const next = new Date()
  next.setDate(next.getDate() + newInt)

  return {
    easeFactor:           newEf,
    interval:             newInt,
    repetitions:          newRep,
    nextReview:           next.toISOString().slice(0, 10),
    consecutiveStruggles: newStruggles,
  }
}

function toQuality(accuracy: number): number {
  if (accuracy >= 0.9) return 5
  if (accuracy >= 0.7) return 4
  if (accuracy >= 0.6) return 3
  if (accuracy >= 0.4) return 2
  if (accuracy >= 0.2) return 1
  return 0
}

// ── Grade helpers ─────────────────────────────────────────────────────────────

/** Upper grade bound for the tier a student belongs to (K-2→2, 3-5→5, 6-8→8, 9-12→12) */
export function tierCeiling(grade: number): number {
  if (grade <= 2)  return 2
  if (grade <= 5)  return 5
  if (grade <= 8)  return 8
  return 12
}

/**
 * Returns up to 5 topics to display per subject.
 * If ≤5 entries, returns all. Otherwise: 3 most recent + 2 weakest not already in recent 3.
 */
export function getTopicDisplay(entries: MasteryEntry[]): MasteryEntry[] {
  if (entries.length <= 5) return entries
  const byRecent  = [...entries].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
  const recent    = byRecent.slice(0, 3)
  const recentSet = new Set(recent.map((e) => e.topic))
  const weakest   = [...entries]
    .filter((e) => !recentSet.has(e.topic))
    .sort((a, b) => a.score - b.score)
    .slice(0, 2)
  return [...recent, ...weakest]
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useMastery(userId: string, gradeLevel = 5) {
  const [mastery,         setMastery]         = useState<MasteryEntry[]>([])
  const [archivedMastery, setArchivedMastery] = useState<MasteryEntry[]>([])
  const [loading,         setLoading]         = useState(true)

  useEffect(() => {
    if (!userId) return
    let mounted = true
    async function load() {
      try {
        // select('*') is intentional — avoids a hard 400 error if a new column
        // (e.g. consecutive_struggles) hasn't been applied to the DB yet.
        // All fields are null-coalesced in the mapping below, so missing
        // columns degrade gracefully to their default values.
        const { data, error } = await supabase
          .from('mastery')
          .select('*')
          .eq('student_id', userId)
          .order('updated_at', { ascending: false })

        if (error) {
          console.error('[Nudge] fetchMastery failed:', error.message)
        }

        if (data && mounted) {
          const floor   = gradeLevel
          const ceiling = tierCeiling(gradeLevel)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const mapped: MasteryEntry[] = data.map((row: any) => ({
            subject:              row.subject,
            topic:                row.topic,
            score:                row.percentage            ?? 0,
            easeFactor:           row.ease_factor           ?? 2.5,
            interval:             row.interval              ?? 0,
            repetitions:          row.repetitions           ?? 0,
            nextReview:           row.next_review           ?? new Date().toISOString().slice(0, 10),
            updatedAt:            row.updated_at            ?? new Date().toISOString(),
            gradeLevel:           row.grade_level           ?? gradeLevel,
            consecutiveStruggles: row.consecutive_struggles ?? 0,
          }))
          setMastery(mapped.filter((e) =>
            (e.gradeLevel ?? gradeLevel) >= floor &&
            (e.gradeLevel ?? gradeLevel) <= ceiling
          ))
          setArchivedMastery(mapped.filter((e) =>
            (e.gradeLevel ?? gradeLevel) < floor
          ))
        }
      } catch {
        // Non-critical — mastery display degrades gracefully
      } finally {
        if (mounted) setLoading(false)
      }
    }
    load()
    return () => { mounted = false }
  }, [userId, gradeLevel])

  /**
   * Called when a session completes — Rule 7 (SM-2 updates on session complete only).
   * @param score  Number of correct answers (out of total)
   * @param total  Total questions in session (default 10)
   */
  const updateMastery = useCallback(async (
    subject: string,
    topic:   string,
    score:   number,
    total  = 10
  ) => {
    const accuracy = score / total
    const newScore = Math.min(100, Math.max(0, Math.round(accuracy * 100)))   // Rule 6: clamp 0-100

    const existing = mastery.find((m) => m.subject === subject && m.topic === topic)

    const sm2 = calculateSM2(
      toQuality(accuracy),
      existing?.easeFactor           ?? 2.5,
      existing?.interval             ?? 0,
      existing?.repetitions          ?? 0,
      existing?.consecutiveStruggles ?? 0   // I3: pass current struggle count
    )

    const entry: MasteryEntry = {
      subject,
      topic,
      score: newScore,
      ...sm2,
      updatedAt: new Date().toISOString(),
    }

    // Optimistic update — replace existing entry or prepend
    setMastery((prev) => [
      entry,
      ...prev.filter((m) => !(m.subject === subject && m.topic === topic)),
    ])

    try {
      await supabase
        .from('mastery')
        .upsert({
          student_id:           userId,
          subject,
          topic,
          grade_level:          gradeLevel,
          percentage:           newScore,
          last_practiced_at:    new Date().toISOString(),
          ease_factor:          sm2.easeFactor,
          interval:             sm2.interval,
          repetitions:          sm2.repetitions,
          next_review:          sm2.nextReview,
          consecutive_struggles: sm2.consecutiveStruggles,  // I3
          updated_at:           entry.updatedAt,
        }, { onConflict: 'student_id,subject,topic,grade_level' })
    } catch (err) {
      console.warn('[Nudge] updateMastery:', err)
    }
  }, [mastery, userId, gradeLevel])

  /** Returns mastery entry for a subject+topic, or null if never practiced */
  const getMastery = useCallback(
    (subject: string, topic: string): MasteryEntry | null =>
      mastery.find((m) => m.subject === subject && m.topic === topic) ?? null,
    [mastery]
  )

  /** Returns all topics whose next review date is today or in the past */
  const getDueTopics = useCallback((): MasteryEntry[] => {
    const today = new Date().toISOString().slice(0, 10)
    return mastery.filter((m) => m.nextReview <= today)
  }, [mastery])

  return { mastery, archivedMastery, loading, updateMastery, getMastery, getDueTopics }
}
